<script setup lang="ts">
import type { Schemas } from "#shopware";
import { getTranslatedProperty } from "@shopware/helpers";

const props = defineProps<{
  productSetItem: Schemas["Product"];
}>();

const { productSetItem } = toRefs(props);

const {
  getAssignedProductData,
  updateItemInSet,
  isItemInCart,
  assignItemToCart,
  removeItemFromCart,
} = useProductSetConfiguration();

const { apiClient } = useShopwareContext();

const assignedProduct = getAssignedProductData(productSetItem.value.id);

watchEffect(() => {
  if (assignedProduct) {
    assignedProduct.selectedQuantity =
      assignedProduct.accessoryQuantity ||
      productSetItem.value.minPurchase ||
      1;
  }
});

const isSelectedOption = (optionId: string) =>
  !!productSetItem.value?.optionIds?.includes(optionId);

const selectOption = async (optionId: string) => {
  if (!assignedProduct) return;

  const selectedOptions = new Set<string>();

  productSetItem.value.optionIds?.forEach((existingOptionId) => {
    const group = productSetItem.value.productConfigurator?.find(
      (group: Schemas["PropertyGroup"]) =>
        group.options?.some((option) => option.id === existingOptionId)
    );

    if (group) {
      const isSameGroup = group.options.some(
        (option: Schemas["PropertyGroupOption"]) => option.id === optionId
      );

      if (isSameGroup) {
        // Replace existing option with the newly selected one
        selectedOptions.add(optionId);
      } else {
        // Keep the previous option for this group
        selectedOptions.add(existingOptionId);
      }
    }
  });

  const variantFound = await findVariantForSelectedOptions(
    Array.from(unref(selectedOptions))
  );

  if (variantFound) {
    updateItemInSet(productSetItem.value.id, variantFound);
  }
};

async function findVariantForSelectedOptions(options?: {
  [code: string]: string;
}): Promise<Schemas["Product"] | undefined> {
  const filter: Schemas["Filters"] = [
    {
      type: "equals",
      field: "parentId",
      value: productSetItem.value.parentId as string,
    },
    ...Object.values(options).map(
      (id) =>
        ({
          type: "equals",
          field: "optionIds",
          value: id as string,
        }) as Schemas["EqualsFilter"]
    ),
  ];
  try {
    const response = await apiClient.invoke("readProduct post /product", {
      body: {
        filter,
        limit: 1,
      },
    });
    return response.data.elements?.[0]; // return first matching product
  } catch (e) {
    console.error("SwProductDetails:findVariantForSelectedOptions", e);
  }
}

const toggleProductToCart = (productId: string) => {
  if (isItemInCart(productId)) {
    removeItemFromCart(productId);
  } else {
    assignItemToCart(productId);
  }
};
</script>
<template>
  <div
    class="bg-cust_cream py-4 md:py-2 ps-[6px] pe-[10px] md:px-6 rounded-xl md:rounded-2xl lg:rounded-3xl items-start"
  >
    <div
      class="flex-wrap md:flex-nowrap flex gap-y-2 md:gap-y-0 gap-[10px] md:gap-4"
    >
      <input
        v-model="assignedProduct.selectedQuantity"
        type="number"
        class="w-8 md:w-10 h-8 md:h-10 font-redit_sans text-center bg-white items-center justify-center flex md:mt-4 rounded border border-solid border-cust_black"
        :min="assignedProduct.accessoryQuantity"
        :max="productSetItem.availableStock"
        :step="1"
      />
      <div class="w-[72px] md:w-[112px] aspect-square">
        <NuxtImg :src="productSetItem?.cover?.media?.url" alt="" />
      </div>
      <div class="w-[calc(100%-124px)] md:w-[calc(100%-305px)] md:pt-3">
        <h5 class="mb-1 font-redit_sans_con font-extrabold">
          {{ getTranslatedProperty(productSetItem, "name") }}
        </h5>
        <div
          class="text-sm md:text-base lg:text-xl line-clamp-2"
          v-html="getTranslatedProperty(productSetItem, 'description')"
        ></div>
      </div>
      <div
        class="w-[calc(100%-124px)] m-0 md:m-0 md:w-[104px] text-end flex md:flex-col md:pt-4 justify-between items-center md:items-end"
        style="margin: 0 0 0 auto"
      >
        <span
          class="w-10 h-10 items-center justify-center inline-flex rounded text-cust_green bg-white md:mb-5 cursor-pointer"
          @click="toggleProductToCart(productSetItem.id)"
        >
          <i
            v-if="isItemInCart(productSetItem.id)"
            class="fa-solid fa-check"
          ></i>
        </span>
        <h5
          class="font-redit_sans_con font-extrabold -order-1 md:order-1 tracking-[0.22px]"
        >
          <SwSharedPrice
            class="justify-start"
            :value="productSetItem?.calculatedPrice?.unitPrice"
          />
        </h5>
      </div>
    </div>
    <div
      v-if="productSetItem.productConfigurator?.length > 0"
      class="pt-4 flex flex-col gap-4 pb-4 mt-2"
    >
      <div
        v-for="(option, index) in productSetItem.productConfigurator"
        class="prod_sm_list w-full flex justify-between"
      >
        <div
          class="flex w-[calc(100%-45px)] md:w-[calc(100%-60px)] gap-2 md:gap-4 items-center"
        >
          <div class="w-8 md:w-10 h-8 md:h-10">&nbsp;</div>
          <h3 class="text-lg text-gray-900 font-medium tracking-wide mb-0">
            {{ option.translated?.name }}
          </h3>
          <div class="flex gap-2 text-base md:text-xl md:gap-4">
            <label
              v-for="optionItem in option.options"
              class="relative cursor-pointer"
              @click="selectOption(optionItem.id)"
            >
              <span
                class="block px-3 md:px-4 py-[5px] md:py-2 border rounded-lg text-gray-700 border-gray-300"
                :class="[
                  isSelectedOption(optionItem.id)
                    ? 'text-white border-green-500 bg-cust_green'
                    : 'bg-white',
                ]"
              >
                {{ optionItem.translated?.name }}
              </span>
            </label>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
